import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Text, Button, Badge, Divider,
  Icon, Flex, Spinner, useDisclosure, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import UserSettingsLayout from './UserSettingsLayout';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { useNavigate } from 'react-router-dom';

const BillingRow = ({ label, sub, children }) => (
  <Flex align="center" justify="space-between" py={4} gap={4} wrap="wrap">
    <Box>
      <Text fontSize="sm" fontWeight="500" color="gray.800">{label}</Text>
      {sub && <Text fontSize="xs" color="gray.400" mt={0.5}>{sub}</Text>}
    </Box>
    {children}
  </Flex>
);

const STATUS_META = {
  paid:          { label: 'Paid',          scheme: 'green'  },
  open:          { label: 'Due',           scheme: 'orange' },
  draft:         { label: 'Draft',         scheme: 'gray'   },
  void:          { label: 'Void',          scheme: 'red'    },
  uncollectible: { label: 'Uncollectible', scheme: 'red'    },
};

const fmtCurrency = (amount, currency = 'nzd') => {
  const symbol = currency.toLowerCase() === 'nzd' ? 'NZ$' : currency.toUpperCase() + ' ';
  return `${symbol}${(amount / 100).toFixed(2)}`;
};

const fmtDate = (unixTs) =>
  new Date(unixTs * 1000).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });

const Billing = () => {
  const [planStatus, setPlanStatus] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [freeMonthlyCredits, setFreeMonthlyCredits] = useState(30);
  const [proMonthlyCredits, setProMonthlyCredits] = useState(300);
  const { isOpen: isCancelOpen, onOpen: onCancelOpen, onClose: onCancelClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Credit counts shown below come from plan_configs (single source of
    // truth, see utils/planTiers.js) — the 30/300 defaults above are only
    // the fallback shown before this resolves, or if it fails.
    fetch('/api/subscription/pricing')
      .then((r) => r.json())
      .then((data) => {
        if (Number.isFinite(data?.freeMonthlyCredits)) setFreeMonthlyCredits(data.freeMonthlyCredits);
        if (Number.isFinite(data?.proMonthlyCredits)) setProMonthlyCredits(data.proMonthlyCredits);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchWithAuth('/api/subscription/status')
      .then((res) => res.json())
      .then((data) => {
        setPlanStatus(data);
        if (data?.hasStripeAccount) {
          fetchWithAuth('/api/subscription/latest-invoice')
            .then((r) => r.json())
            .then(setInvoiceData)
            .catch(() => {});
        }
      })
      .catch((err) => console.error('Error fetching plan status:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const openPortal = async () => {
    setIsPortalLoading(true);
    try {
      const response = await fetchWithAuth('/api/subscription/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Couldn't open billing portal", description: data.error || 'Please try again.', status: 'error', duration: 4000, isClosable: true });
      }
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again later.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    setIsCancelling(true);
    try {
      const response = await fetchWithAuth('/api/subscription/cancel-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        toast({ title: 'Plan cancelled', description: "You've been downgraded to the Free plan.", status: 'success', duration: 5000, isClosable: true });
        onCancelClose();
        window.location.reload();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.error || 'Failed to cancel plan', status: 'error', duration: 5000, isClosable: true });
      }
    } catch {
      toast({ title: 'Error', description: 'Something went wrong. Please try again.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsCancelling(false);
    }
  };

  const isPro = planStatus?.planType === 'pro';
  const hasStripeAccount = !!planStatus?.hasStripeAccount;
  const renewalDate = planStatus?.subscriptionPeriodEnd
    ? new Date(planStatus.subscriptionPeriodEnd).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  if (isLoading) {
    return (
      <UserSettingsLayout>
        <Flex align="center" justify="center" minH="300px">
          <Spinner size="lg" color="brand.600" thickness="3px" />
        </Flex>
      </UserSettingsLayout>
    );
  }

  return (
    <UserSettingsLayout>
      <Box width="100%" bg="white" px={{ base: 5, md: 10 }} py={8}>
        <Box mb={8} maxW="680px">
          <Text fontSize="xl" fontWeight="700" color="brand.700">Billing</Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Manage your plan and payment details.
          </Text>
        </Box>

        <VStack align="stretch" width="100%" maxW="680px" spacing={6}>

          {/* ── Current Plan ─────────────────────────────────────── */}
          <Box bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" px={6} py={2}>
            <BillingRow
              label="Current plan"
              sub={isPro && renewalDate ? `Renews ${renewalDate}` : isPro ? 'Active' : 'Free forever'}
            >
              <HStack spacing={3}>
                <Badge
                  px={3} py={1} borderRadius="full" fontSize="sm"
                  colorScheme={isPro ? 'brand' : 'gray'}
                  textTransform="capitalize"
                >
                  {planStatus?.planType || 'free'}
                </Badge>
                {isPro && (
                  <Text fontSize="sm" fontWeight="600" color="gray.700">NZ$19.99/mo</Text>
                )}
              </HStack>
            </BillingRow>

            <Divider />

            <BillingRow
              label="Change plan"
              sub={isPro ? 'Downgrade to Free or adjust your subscription' : 'Unlock Pro features'}
            >
              {isPro ? (
                <Button
                  size="sm" variant="ghost" color="red.400"
                  _hover={{ bg: 'red.50', color: 'red.600' }}
                  onClick={onCancelOpen}
                >
                  Cancel plan
                </Button>
              ) : (
                <Button
                  size="sm" bg="gray.900" color="white" borderRadius="lg"
                  _hover={{ bg: 'gray.700' }} fontWeight="600"
                  onClick={() => navigate('/upgrade')}
                >
                  Upgrade to Pro →
                </Button>
              )}
            </BillingRow>
          </Box>

          {/* ── Payment & Billing (Pro only) ──────────────────────── */}
          {isPro && (
            <Box bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" px={6} py={2}>
              {invoiceData?.invoice && (
                <Box pt={4} pb={3} borderBottom="1px" borderColor="gray.200">
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge
                      colorScheme={STATUS_META[invoiceData.invoice.status]?.scheme || 'gray'}
                      borderRadius="full" px={2} py={0.5} fontSize="xs" textTransform="capitalize"
                    >
                      {STATUS_META[invoiceData.invoice.status]?.label || invoiceData.invoice.status}
                    </Badge>
                    <Text fontSize="xs" color="gray.600" fontWeight="500">
                      {fmtCurrency(invoiceData.invoice.amount, invoiceData.invoice.currency)}
                    </Text>
                    <Text fontSize="xs" color="gray.400">·</Text>
                    <Text fontSize="xs" color="gray.400">{fmtDate(invoiceData.invoice.date)}</Text>
                    {invoiceData.invoice.description && (
                      <>
                        <Text fontSize="xs" color="gray.400">·</Text>
                        <Text fontSize="xs" color="gray.400" noOfLines={1}>{invoiceData.invoice.description}</Text>
                      </>
                    )}
                  </HStack>
                </Box>
              )}
              <BillingRow
                label="Payment method"
                sub="Update your card or billing details"
              >
                <Button
                  size="sm" variant="outline" borderRadius="lg"
                  rightIcon={<ExternalLinkIcon boxSize={3} />}
                  onClick={openPortal}
                  isLoading={isPortalLoading}
                  loadingText="Opening..."
                >
                  Update
                </Button>
              </BillingRow>

              <Divider />

              <BillingRow
                label="Invoices & receipts"
                sub="View and download past invoices"
              >
                <Button
                  size="sm" variant="outline" borderRadius="lg"
                  rightIcon={<ExternalLinkIcon boxSize={3} />}
                  onClick={openPortal}
                  isLoading={isPortalLoading}
                >
                  View invoices
                </Button>
              </BillingRow>

              <Divider />

              <BillingRow
                label="Billing management"
                sub="Full control via Stripe — cancel, change card, download receipts"
              >
                <Button
                  size="sm" bg="gray.900" color="white" borderRadius="lg"
                  _hover={{ bg: 'gray.700' }}
                  rightIcon={<ExternalLinkIcon boxSize={3} />}
                  onClick={openPortal}
                  isLoading={isPortalLoading}
                  loadingText="Opening..."
                >
                  Manage billing
                </Button>
              </BillingRow>
            </Box>
          )}

          {/* ── Billing portal for free users who have topped up ─── */}
          {!isPro && hasStripeAccount && (
            <Box bg="gray.50" borderRadius="xl" border="1px" borderColor="gray.200" px={6} py={2}>
              {invoiceData?.invoice && (
                <Box pt={4} pb={3} borderBottom="1px" borderColor="gray.200">
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge
                      colorScheme={STATUS_META[invoiceData.invoice.status]?.scheme || 'gray'}
                      borderRadius="full" px={2} py={0.5} fontSize="xs" textTransform="capitalize"
                    >
                      {STATUS_META[invoiceData.invoice.status]?.label || invoiceData.invoice.status}
                    </Badge>
                    <Text fontSize="xs" color="gray.600" fontWeight="500">
                      {fmtCurrency(invoiceData.invoice.amount, invoiceData.invoice.currency)}
                    </Text>
                    <Text fontSize="xs" color="gray.400">·</Text>
                    <Text fontSize="xs" color="gray.400">{fmtDate(invoiceData.invoice.date)}</Text>
                    {invoiceData.invoice.description && (
                      <>
                        <Text fontSize="xs" color="gray.400">·</Text>
                        <Text fontSize="xs" color="gray.400" noOfLines={1}>{invoiceData.invoice.description}</Text>
                      </>
                    )}
                  </HStack>
                </Box>
              )}
              <BillingRow
                label="Top-up receipts"
                sub="View and download receipts for your credit purchases"
              >
                <Button
                  size="sm" variant="outline" borderRadius="lg"
                  rightIcon={<ExternalLinkIcon boxSize={3} />}
                  onClick={openPortal}
                  isLoading={isPortalLoading}
                  loadingText="Opening..."
                >
                  View receipts
                </Button>
              </BillingRow>

              <Divider />

              <BillingRow
                label="Billing management"
                sub="Manage your payment details via Stripe"
              >
                <Button
                  size="sm" bg="gray.900" color="white" borderRadius="lg"
                  _hover={{ bg: 'gray.700' }}
                  rightIcon={<ExternalLinkIcon boxSize={3} />}
                  onClick={openPortal}
                  isLoading={isPortalLoading}
                  loadingText="Opening..."
                >
                  Manage billing
                </Button>
              </BillingRow>
            </Box>
          )}

          {/* ── Free plan upgrade nudge ───────────────────────────── */}
          {!isPro && (
            <Box
              bg="white" borderRadius="xl" border="2px" borderColor="brand.100"
              p={6} position="relative" overflow="hidden"
            >
              <Box
                position="absolute" top={0} right={0}
                w="120px" h="120px"
                bg="brand.50" borderRadius="full"
                transform="translate(40px, -40px)"
              />
              <Text fontSize="md" fontWeight="700" color="gray.900" mb={1}>
                Get more with Pro
              </Text>
              <Text fontSize="sm" color="gray.500" mb={4} maxW="400px">
                Unlock {proMonthlyCredits} credits/month, 10 projects, unlimited AI chats, enriched competitor data, and more. Free plan includes {freeMonthlyCredits} credits/month.
              </Text>
              <VStack align="start" spacing={2} mb={5}>
                {[
                  `${proMonthlyCredits} AI credits / month (vs ${freeMonthlyCredits} on Free)`,
                  '10 projects + extra slots add-on',
                  'Unlimited AI strategy chats',
                  'Enriched competitor data',
                  'Personalised branded public shares',
                ].map(f => (
                  <HStack key={f} spacing={2}>
                    <Icon as={CheckIcon} color="brand.500" boxSize={3} />
                    <Text fontSize="sm" color="gray.600">{f}</Text>
                  </HStack>
                ))}
              </VStack>
              <HStack>
                <Button
                  bg="gray.900" color="white" borderRadius="xl"
                  _hover={{ bg: 'gray.700' }} fontWeight="700"
                  onClick={() => navigate('/upgrade')}
                >
                  Upgrade to Pro — NZ$19.99/mo →
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.400" mt={2}>Billed monthly · Cancel anytime · No lock-in</Text>
            </Box>
          )}

        </VStack>
      </Box>

      {/* Cancel confirmation modal */}
      <Modal isOpen={isCancelOpen} onClose={onCancelClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(3px)" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader>Cancel Pro Plan?</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Text color="gray.600" mb={4}>
              You'll lose access to these Pro features at the end of your billing period:
            </Text>
            <VStack align="start" spacing={2} mb={4}>
              {[
                `${proMonthlyCredits} credits / month`,
                'Unlimited AI conversations',
                'Priority AI processing',
                'Enriched competitor analysis',
                'Extra project slots',
              ].map(f => (
                <HStack key={f} spacing={2}>
                  <Icon as={WarningIcon} color="orange.400" boxSize={4} />
                  <Text fontSize="sm" color="gray.700">{f}</Text>
                </HStack>
              ))}
            </VStack>
            <Text fontSize="sm" color="gray.500">
              Your data and projects are preserved. You'll revert to Free tier limits.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onCancelClose}>Keep Pro</Button>
            <Button colorScheme="red" onClick={handleCancelPlan} isLoading={isCancelling} loadingText="Cancelling...">
              Cancel Plan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </UserSettingsLayout>
  );
};

export default Billing;
